/**
 * Jun 2026 — Partner Help & Guide (/partner/guide).
 *
 * Task-focused quick-start manual for PWS partners. One card per
 * real-world workflow, plain language, plus a Download/Print PDF
 * button (browser print with a print-only stylesheet).
 */
import { PartnerShell } from "./Dashboard";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";

const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #partner-guide-print, #partner-guide-print * { visibility: visible; }
  #partner-guide-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0 8mm; }
  #partner-guide-print a { color: #0f172a; text-decoration: none; }
  .guide-section { break-inside: avoid; }
}`;

const SECTIONS = [
  {
    id: "getting-started",
    title: "1 · Getting started",
    body: [
      ["Sign in", "Go to the partner login page and use the email address your portal invite was sent to. Forgot your password? Use the reset link on the login page."],
      ["Complete your setup", "Before leads can flow smoothly, check three pages: Business Details (your trading name, contact details and the notification email where leads and reports are sent), Payment Details (where reimbursements and payouts go), and My Territory (the suburbs and postcodes you cover)."],
      ["Your notification email matters", "Quote requests and published-report alerts are sent to the notification email on your Business Details page — keep it set to the inbox your team actually monitors."],
    ],
  },
  {
    id: "dashboard",
    title: "2 · Your Dashboard at a glance",
    body: [
      ["What you'll see", "Tiles for total bookings in your territory, new bookings, samples awaiting return, results pending, results published, and anything flagged. Click any tile area to jump to the matching list."],
      ["Where bookings come from", "Bookings reach you three ways: your personal partner link, a customer the system remembered from a previous visit, and region allocation (any customer in your territory who books through the national site)."],
    ],
  },
  {
    id: "leads",
    title: "3 · When a quote request arrives",
    body: [
      ["You'll get an email", "When a customer taps \u201cRequest Instant Estimate\u201d on their water report, the lead is sent to your notification email and appears in Quote Requests."],
      ["Act fast", "The lead includes the customer's name, phone, email, property address, booking reference and the recommended actions from their report — everything you need to call them while the report is fresh in their mind."],
      ["Work the lead", "Open the lead in Quote Requests to see the full context and update its status as you contact, quote and win the job."],
    ],
  },
  {
    id: "bookings",
    title: "4 · Bookings in your territory",
    body: [
      ["Nothing to do for mail-outs", "When AWARE posts the kit, we handle postage, return and lab processing. You simply see the booking progress in your Bookings list."],
      ["Partner-supplied kits", "If you hand a kit from your own stock directly to a customer, make sure the booking is marked partner-supplied — you're automatically credited the $29 kit reimbursement in your ledger once the booking is paid."],
      ["Track progress", "Each booking shows where the sample is: kit sent, awaiting return, at the lab, results pending, report published."],
    ],
  },
  {
    id: "kits",
    title: "5 · Sample kits & stock",
    body: [
      ["Order kits", "Use Order Sample Kits to top up your stock. Kits are delivered to your registered business address."],
      ["Get reimbursed", "Every time a paid booking uses a kit you supplied, a $29 reimbursement is recorded automatically in your Ledger — no paperwork needed."],
    ],
  },
  {
    id: "reports",
    title: "6 · When a report is published",
    body: [
      ["You're notified separately", "The customer receives their report by email, and you independently receive a partner notification for every published report in your territory — with a link to the customer record."],
      ["Follow up", "Open Reports or the customer's page to see their results and recommended actions. Reports flagging treatment needs are your best follow-up opportunities."],
    ],
  },
  {
    id: "customers",
    title: "7 · Customers & Water Passports",
    body: [
      ["Customers", "Every customer who books in your territory appears in Customers, with their bookings, reports and contact details in one place."],
      ["Water Passports", "A property's full testing history lives in its Water Passport — ideal for repeat customers, rentals and pre-purchase conversations."],
    ],
  },
  {
    id: "payouts",
    title: "8 · Getting paid",
    body: [
      ["Ledger & Payouts", "Your ledger lists every commission and kit reimbursement as it's earned, with running totals and payout status."],
      ["Payment details", "Keep your bank details current on the Payment Details page so payouts are never delayed."],
    ],
  },
  {
    id: "marketing",
    title: "9 · Marketing & documents",
    body: [
      ["Marketing Resources", "Ready-made social tiles, flyers and your personal booking link — share the link anywhere; every booking through it is attributed to you."],
      ["Documents", "Agreements, price lists and operational documents shared with you by AWARE live under Documents."],
    ],
  },
  {
    id: "help",
    title: "10 · Need help?",
    body: [
      ["Contact AWARE", "Anything unclear, or something in the portal not behaving? Email the AWARE team — include the booking reference if your question is about a specific job."],
    ],
  },
];

export default function PartnerGuide() {
  return (
    <PartnerShell active="help & guide">
      <style>{PRINT_CSS}</style>
      <div id="partner-guide-print" data-testid="partner-guide-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Partner Portal — Quick-Start Guide</h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Everything you need to run your territory, one task at a time. Bookmark this page —
              it's always up to date with the portal.
            </p>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="partner-guide-print-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-700"
          >
            <Printer className="w-4 h-4" /> Download / Print PDF
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="partner-guide-toc">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="px-3 py-1 rounded-full bg-white border border-slate-200 text-[12px] font-medium text-slate-600 hover:border-cyan-500 hover:text-cyan-700"
            >
              {s.title.replace(/^\d+ · /, "")}
            </a>
          ))}
        </div>

        <div className="mt-6 grid gap-5">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`partner-guide-section-${s.id}`}
              className="guide-section bg-white rounded-2xl border border-slate-200 p-6 scroll-mt-24"
            >
              <h2 className="text-lg font-bold text-slate-900">{s.title}</h2>
              <dl className="mt-3 space-y-3">
                {s.body.map(([heading, text]) => (
                  <div key={heading}>
                    <dt className="text-sm font-semibold text-slate-800">{heading}</dt>
                    <dd className="text-sm text-slate-600 leading-relaxed">{text}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <p className="mt-6 text-[11px] text-slate-400">
          Quick links:{" "}
          <Link to="/partner/business-details" className="underline">Business Details</Link> ·{" "}
          <Link to="/partner/payment-details" className="underline">Payment Details</Link> ·{" "}
          <Link to="/partner/kit-orders" className="underline">Order Sample Kits</Link> ·{" "}
          <Link to="/partner/ledger" className="underline">Ledger &amp; Payouts</Link>
        </p>
      </div>
    </PartnerShell>
  );
}
